import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { TiArrowSortedDown, TiArrowSortedUp } from 'react-icons/ti';
import { GlobalContext } from '../../context/GlobalContext';
import { ModifyInspectionsContext } from '../../context/ModifyInspectionsContext';
import Search from '../Search/Search';
import TableTabs from './TableTabs';
import { global } from '../../shared/colors';
import { COMPLETE_STATUS_ID } from '../../shared/constants';

const CommonStyles = `
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction: column;
    background: ${global.Gray6};
    border-radius: 20px;
`;

const StyledWrapper = styled.div`
    ${CommonStyles}
`;

const StyledEmptyState = styled(StyledWrapper)`
    h1 {
        font-weight: 600;
        font-size: 32px;
        color: ${global.Black};
        margin-top: 90px;
        margin-bottom: 32px;
    }
    p {
        font-weight: 400;
        font-size: 24px;
        color: ${global.Gray3};
    }
`;

const StyledEmptyFiles = styled(StyledWrapper)`
    width: 100%;

    h1 {
        font-size: 24px;
        font-weight: 600;
        color: black;
        margin-bottom: 28px;
    }

    p {
        margin: 0 auto;
        font-size: 20px;
        color: ${global.Gray4};
        font-weight: 400;
    }
`;

const StyledTable = styled.table`
    width: 100%;
    border-spacing: 0;
    border-collapse: collapse;
    display: list-item;
    height: 90%;
    overflow: auto;
    thead {
        position: sticky
        z-index: 10;
        top: 0;
        th {
            z-index: 10;
            height: 51px;
            div {
                align-items: center;
            }
        }
        th:first-child {
            border-top-left-radius: 10px;
            border-bottom-left-radius: 10px;
        }
    }
    tr {
        display: table;
        width: 100%;
        table-layout: fixed;
    }

    &::-webkit-scrollbar {
        width: 3px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
        margin-top: 50px;
    }
    &::-webkit-scrollbar-thumb {
        background-color: ${global.Gray6};
        border-radius: 2px;
    }
`;

const StyledTh = styled.th`
    background: ${global.White};
    padding: 10px;
    text-align: left;
    width: ${(props) => props.width || 'auto'};
    cursor: ${(props) => (props.isSortable ? 'pointer' : 'default')};
    height: 31px;
    &:hover {
        opacity: 60%;
    }
    &:first-child {
        border-top-left-radius: 10px;
        border-bottom-left-radius: 10px;
    }
    &:last-child {
        border-top-right-radius: 10px;
        border-bottom-right-radius: 10px;
    }
`;

const StyledTr = styled.tr`
    background: ${global.Gray6};
    height: 51px;
    &:nth-child(even) {
        background: ${global.White};
        border-radius: 10px;
    }
    &:hover {
        background-color: ${global.Gray5};
        cursor: pointer;
        border-radius: 10px;
    }
`;

const StyledTd = styled.td`
    padding: 10px;
    width: ${(props) => props.width || 'auto'};
    &:first-child {
        border-top-left-radius: 10px;
        border-bottom-left-radius: 10px;
    }
    &:last-child {
        border-top-right-radius: 10px;
        border-bottom-right-radius: 10px;
    }
`;

const ArrowContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    svg:first-child {
        margin-bottom: -8px;
    }
`;

const InnerHeader = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
    padding: 0 20px 0 0;
`;

const SelectAllCheckboxContainer = styled.div`
    position: relative;
    top: -30px;
    left: 11px;
    input[type='checkbox'] {
        margin: 0;
        cursor: pointer;
        visibility: hidden;
        width: 16px;
        height: 16px;
    }

    .custom-checkbox {
        height: 16px;
        width: 16px;
        display: block;
        background-color: ${global.White};
        border-radius: 5px;
        border: 1px solid ${global.Gray5};
        margin-top: -20px;
        margin-left: 4px;
    }

    .checkbox-group input:checked ~ .custom-checkbox {
        background-color: rgba(10, 132, 255, 0.15);
        border-color: ${global.PrimaryBlue};
    }

    .custom-checkbox:after {
        content: '';
        display: none;
    }

    .checkbox-group input:checked ~ .custom-checkbox:after {
        display: block;
    }

    .checkbox-group .custom-checkbox:after {
        position: relative;
        z-index: 0;
        margin: 2px auto;
        width: 2px;
        height: 6px;
        border: solid ${global.PrimaryBlue};
        border-width: 0 2px 2px 0;
        -webkit-transform: rotate(45deg);
        -ms-transform: rotate(45deg);
        transform: rotate(45deg);
    }
    width: fit-content;
`;

const StyledDiv = styled.div`
    background: ${global.Gray6};
    padding: ${(props) => (props.tabsEnabled ? '20px' : '40px')};
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: 100%;
`;

const CheckboxTd = styled(StyledTd)`
    width: 40px;
    text-align: center;
    padding: 10px;
    input[type='checkbox'] {
        margin: 0;
        cursor: pointer;
        visibility: hidden;
        width: 16px;
        height: 16px;
    }

    .custom-checkbox {
        height: 16px;
        width: 16px;
        display: block;
        background-color: ${global.White};
        border-radius: 5px;
        border: 1px solid ${global.Gray5};
        margin-top: -20px;
        margin-left: 4px;
    }

    .checkbox-group input:checked ~ .custom-checkbox {
        background-color: rgba(10, 132, 255, 0.15);
        border-color: ${global.PrimaryBlue};
    }

    .custom-checkbox:after {
        content: '';
        display: none;
    }

    .checkbox-group input:checked ~ .custom-checkbox:after {
        display: block;
    }

    .checkbox-group .custom-checkbox:after {
        position: relative;
        z-index: 0;
        margin: 2px auto;
        width: 2px;
        height: 6px;
        border: solid ${global.PrimaryBlue};
        border-width: 0 2px 2px 0;
        -webkit-transform: rotate(45deg);
        -ms-transform: rotate(45deg);
        transform: rotate(45deg);
    }
`;

const StyledSection = styled.section`
    height: 98%;
`;

const AscIcon = ({ isActive }) => (
  <TiArrowSortedUp size={16} color={isActive ? global.Black : global.Gray5} />
);

const DescIcon = ({ isActive }) => (
  <TiArrowSortedDown
    size={16}
    color={isActive ? global.Black : global.Gray5}
  />
);

const NeutralIcon = () => (
  <ArrowContainer>
    <AscIcon isActive={false} />
    <DescIcon isActive={false} />
  </ArrowContainer>
);

const Table = ({
  data: initialTableData,
  columns: tableColumns,
  completedColumns,
  onRow,
  checkbox,
  enableTabs,
  isUpload,
  searchable,
  fileSearch,
  searchPlaceholder,
  onRowSelectionChange = () => null
}) => {
  const { customerId } = useParams();
  const [filter, setFilter] = useState('');
  const [tableData, setTableData] = useState(initialTableData);
  const [sortState, setSortState] = useState({
    key: null,
    direction: null
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const { inspections, is_admin } = useContext(GlobalContext);
  const { clearFields, resetFormLocation } = useContext(
    ModifyInspectionsContext
  );
  const navigate = useNavigate();
  const filteredInspections = customerId
    ? inspections?.filter(
      (inspection) => inspection.customerId.toString() === customerId
    )
    : inspections;

  const handleInProgress = (record) => {
    clearFields();
    resetFormLocation();
    navigate(`/inspections/${record?.id}`);
  };

  const isAllSelected = tableData?.length > 0 && selectedRows?.length === tableData?.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(tableData?.map((item) => item.id));
    }
  };

  const handleSelectRow = (id, event) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedRows((prevSelected) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((selectedId) => selectedId !== id);
      }
      return [...prevSelected, id];
    });
  };

  const sortData = (data, key, direction) => [...data].sort((a, b) => {
    let aValue = a[key];
    let bValue = b[key];

    if (Date.parse(aValue) && Date.parse(bValue)) {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    } else if (
      typeof aValue === 'number'
      && typeof bValue === 'number'
    ) {
      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    } else {
      aValue = String(aValue);
      bValue = String(bValue);
    }

    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  const onRowClick = (rowData) => {
    onRow(rowData);
  };

  const onSort = (columnKey) => {
    const column = tableColumns.find((col) => col.key === columnKey);
    if (column && column.sortable) {
      if (sortState.key === columnKey && sortState.direction === 'asc') {
        setSortState({ key: columnKey, direction: 'desc' });
      } else if (
        sortState.key === columnKey
        && sortState.direction === 'desc'
      ) {
        setSortState({ key: null, direction: null });
      } else {
        setSortState({ key: columnKey, direction: 'asc' });
      }
    }
  };

  const renderSortIcon = (columnKey) => {
    if (sortState.key === columnKey) {
      return (
        <ArrowContainer>
          <AscIcon isActive={sortState.direction === 'asc'} />
          <DescIcon isActive={sortState.direction === 'desc'} />
        </ArrowContainer>
      );
    }
    return <NeutralIcon />;
  };

  const renderHeaders = (columns) => [
    ...columns.map((column) => (
      <StyledTh
        key={column.key}
        width={column.width}
        isSortable={column.sortable}
        onClick={() => column.sortable && onSort(column.key)}
      >
        <InnerHeader>
          {column.title}
          {column.sortable && renderSortIcon(column.key)}
        </InnerHeader>
      </StyledTh>
    ))
  ];

  const renderRows = (data) => data?.map((item) => (
    <StyledTr key={item.id} onClick={() => onRowClick(item)}>
      {checkbox ? (
        <CheckboxTd>
          <label className="checkbox-group">
            <input
              type="checkbox"
              checked={selectedRows.includes(item.id)}
              onChange={(event) => handleSelectRow(item.id, event)}
            />
            <span className="custom-checkbox" />
          </label>
        </CheckboxTd>
      ) : null}
      {tableColumns?.map((column) => (
        <StyledTd key={column.key} width={column.width}>
          {column.render
            ? column.render(item[column.dataIndex], item)
            : item[column.dataIndex]}
        </StyledTd>
      ))}
    </StyledTr>
  ));

  useEffect(() => {
    let filteredData = initialTableData;

    if (filter) {
      filteredData = initialTableData.filter((item) => Object.values(item)
        .join(' ')
        .toLowerCase()
        .includes(filter.toLowerCase()));
    }

    if (sortState.key && sortState.direction) {
      filteredData = sortData(
        filteredData,
        sortState.key,
        sortState.direction
      );
    }

    setTableData(filteredData);
  }, [sortState, filter, initialTableData]);

  useEffect(() => {
    onRowSelectionChange(selectedRows);
  }, [selectedRows]);

  const renderContent = () => {
    if (isUpload && initialTableData?.length === 0) {
      return (
        <StyledEmptyFiles>
          <h1>No files found</h1>
          <p>You have no files for this type.</p>
        </StyledEmptyFiles>
      );
    }

    const renderEmptyState = (isAdmin) => {
      const title = isAdmin
        ? 'Create an inspection'
        : 'No Inspection Data available';
      const message = isAdmin
        ? 'Create an inspection report to see completed inspections here.'
        : 'This table is currently empty as no inspections have been initiated.';

      return (
        <StyledEmptyState>
          <h1>{title}</h1>
          <p>{message}</p>
        </StyledEmptyState>
      );
    };

    if (initialTableData?.length === 0 && inspections?.length === 0) {
      return renderEmptyState(is_admin);
    }

    if (enableTabs) {
      return (
        <TableTabs
          onRowCompleted={onRow}
          handleInProgress={handleInProgress}
          data={[
            filteredInspections?.filter(
              (item) => item?.status?.code !== null
                && item?.status?.code !== 'complete'
                && item?.status?.code !== 'deleted'
            ),
            initialTableData.filter(
              (item) => item?.status?.id === null
                || item?.status?.id === COMPLETE_STATUS_ID
                || item?.status?.code === 'complete'
            )
          ]}
          counts={[
            filteredInspections?.filter(
              (item) => item?.status?.code !== null
                && item?.status?.code !== 'complete'
                && item?.status?.code !== 'deleted'
            )?.length,
            initialTableData.filter(
              (item) => item.status?.code === null
            )?.length
          ]}
          filters={[
            {
              title: 'In progress',
              filter: '',
              columns: tableColumns
            },
            {
              title: 'Complete',
              filter: '',
              columns: completedColumns
            }
          ]}
          tableComponent={Table}
        />
      );
    }

    return (
      <StyledSection>
        {searchable
          && (fileSearch ? (
            <Search
              filter={filter}
              setFilter={setFilter}
              fileSearch={fileSearch}
              placeholderText={searchPlaceholder}
            />
          ) : (
            <Search filter={filter} setFilter={setFilter} />
          ))}
        {checkbox ? (
          <SelectAllCheckboxContainer>
            <label className="checkbox-group">
              <input
                id="SelectAllCheckbox"
                type="checkbox"
                checked={isAllSelected}
                onChange={handleSelectAll}
              />
              <span className="custom-checkbox" />
            </label>
          </SelectAllCheckboxContainer>
        ) : null}
        <StyledTable>
          <thead>
            <tr>{renderHeaders(tableColumns)}</tr>
          </thead>
          <tbody>{renderRows(tableData)}</tbody>
        </StyledTable>
      </StyledSection>
    );
  };

  return <StyledDiv tabsEnabled={enableTabs}>{renderContent()}</StyledDiv>;
};

const columnShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  width: PropTypes.string,
  sortable: PropTypes.bool,
  render: PropTypes.func,
  dataIndex: PropTypes.string.isRequired
});

const inspectionShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  createdAt: PropTypes.string,
  createdBy: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  flogistixId: PropTypes.string,
  createdByEmail: PropTypes.string,
  createdByName: PropTypes.string,
  deletedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inspectionDate: PropTypes.string,
  statusId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  statusName: PropTypes.string,
  complianceType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  state: PropTypes.string,
  county: PropTypes.string,
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  gps: PropTypes.string,
  inspectionFrequency: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  operationalStatus: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  locationSignFileId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  filesCount: PropTypes.number,
  customerName: PropTypes.string,
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  customerLocationName: PropTypes.string,
  customerLocationId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  inspections: PropTypes.number,
  recent: PropTypes.string,
  key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
});

const dataShape = PropTypes.shape({
  inspections: PropTypes.arrayOf(inspectionShape).isRequired,
  executionTime: PropTypes.string.isRequired
});

Table.propTypes = {
  data: dataShape.isRequired,
  columns: PropTypes.arrayOf(columnShape).isRequired,
  completedColumns: PropTypes.arrayOf(columnShape),
  onRow: PropTypes.func,
  checkbox: PropTypes.bool,
  enableTabs: PropTypes.bool,
  isUpload: PropTypes.bool,
  searchable: PropTypes.bool,
  fileSearch: PropTypes.bool,
  onRowSelectionChange: PropTypes.func,
  searchPlaceholder: PropTypes.string
};

Table.defaultProps = {
  checkbox: false,
  enableTabs: false,
  isUpload: false,
  searchable: true,
  onRowSelectionChange: () => null,
  completedColumns: {},
  fileSearch: null,
  onRow: () => null,
  searchPlaceholder: 'Filter by column title'
};

export default Table;
